import { useState } from 'react';
import { getDomainID, qaBackendApiInstance } from '@src/config/config';
import { NODE_ENV } from '@src/config';
import { useQueryClient } from 'react-query';

interface UploadResponse {
  key: string;
  url: string;
}

interface KeyImagesResponse {
  key: string;
  url: string;
}

const isProd = NODE_ENV !== 'dev';

async function keyImagesAPI(
  file: File | null, 
  asset_name: string, 
  domain_id: string, 
  isPublish?: boolean
): Promise<KeyImagesResponse | undefined> {
  if (!file) return undefined;

  const file_extension = file.name.split('.').pop();
  if (!file_extension) {
    throw new Error('File extension is missing.');
  }

  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
  const videoExtensions = ['mp4', 'mov', 'avi', 'mkv', 'webm'];
  
  let asset_type: 'image' | 'video';
  if (imageExtensions.includes(file_extension.toLowerCase())) {
    asset_type = 'image';
  } else if (videoExtensions.includes(file_extension.toLowerCase())) {
    asset_type = 'video';
  } else {
    throw new Error('Unsupported file type');
  }

  try {
    const response = await qaBackendApiInstance.post<KeyImagesResponse>('/aRole/generate-presigned-url', {
      domain_id,
      asset_type,
      filename: file.name,
      content_type: file.type,
      asset_name,
      isPublish: isPublish ?? false
    });
    return response.data;
  } catch (error) {
    console.error('Error generating upload key:', error);
    return undefined;
  }
}

export function useMultiPartServerS3() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const uploadImage = async (
    file: File | null, 
    asset_name: string, 
    progressCallback: (progress: number) => void, 
    isPublish?: boolean
  ): Promise<any> => {
    setLoading(true);
    setError(null);
    
    try {
      const domain_id = getDomainID();
      const generatedData = await keyImagesAPI(file, asset_name, domain_id, isPublish);
      
      if (!generatedData?.url || !file) {
        throw new Error('Failed to generate upload URL');
      }

      // Upload file using the presigned URL
      const uploadRes = await fetch(generatedData.url, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type
        },
        body: file
      });

      if (!uploadRes.ok) {
        throw new Error('Failed to upload asset');
      }

      // Simulate progress since we can't track actual progress with presigned URLs
      progressCallback(100);

      const result = {
        success: true,
        filename: file.name,
        asset_type: file.type.startsWith('image/') ? 'image' : 'video',
        key: generatedData.key,
        url: generatedData.url
      };

      setData(result);
      await queryClient.invalidateQueries(['get-all-aRole-assets']);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      console.error('Upload error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { uploadImage, loading, data, error };
}
